import type { Batch, PaymentMethod, ReferralWithdrawal, User } from '@/api/entities';
import type { ReferralResponse } from '@/api/user';
import { getPaymentMethods, getPurchasedBatches, getReferrals, getUserData, getWithdrawals } from '@/api/user';
import { isActionError } from '@/lib/error';
import { useQueries } from '@tanstack/react-query';

/** Consistent query keys for caching and data management */
export const PROFILE_QUERY_KEYS = {
  user: ['user'],
  paymentMethods: ['paymentMethods'],
  purchasedBatches: ['purchasedBatches'],
  referrals: ['referrals'],
  withdrawals: ['withdrawals']
} as const;

/** Configuration for each profile-related query */
const QUERY_CONFIG = {
  staleTime: 1000 * 60 * 5,
  // 5 minutes
  refetchOnWindowFocus: false
};

/** Type for initial data passed to the hook */
export interface ProfileInitialData {
  user: User;
  paymentMethods: PaymentMethod;
  purchasedBatches: {
    batches?: Batch[];
  };
  referrals?: ReferralResponse;
  withdrawals?: ReferralWithdrawal[];
}

/**
 * Manages profile-related data fetching and state in parallel
 * @param initialData Server-side fetched initial data
 * @param initialData.user
 * @param initialData.paymentMethods
 * @param initialData.purchasedBatches
 * @param initialData.referrals
 * @param initialData.withdrawals
 * @returns Processed profile data with React Query management
 */
export function useProfileData(initialData: ProfileInitialData) {
  const [{
    data: user
  }, {
    data: paymentMethods
  }, {
    data: purchasedBatches
  }, {
    data: referrals
  }, {
    data: withdrawals
  }] = useQueries({
    queries: [{
      queryKey: PROFILE_QUERY_KEYS.user,
      queryFn: async () => {
        const response = await getUserData();
        if (isActionError(response)) {
          throw new Error(response.error);
        }
        return response;
      },
      initialData: initialData.user,
      placeholderData: initialData.user,
      ...QUERY_CONFIG,
      retry: 1
    }, {
      queryKey: PROFILE_QUERY_KEYS.paymentMethods,
      queryFn: async () => {
        const response = await getPaymentMethods();
        if (isActionError(response)) {
          throw new Error(response.error);
        }
        return response;
      },
      initialData: initialData.paymentMethods,
      placeholderData: initialData.paymentMethods,
      ...QUERY_CONFIG,
      retry: 1
    }, {
      queryKey: PROFILE_QUERY_KEYS.purchasedBatches,
      queryFn: async () => {
        const response = await getPurchasedBatches();
        if (isActionError(response)) {
          throw new Error(response.error);
        }
        return response;
      },
      initialData: initialData.purchasedBatches.batches ? {
        batches: initialData.purchasedBatches.batches
      } : undefined,
      placeholderData: initialData.purchasedBatches.batches ? {
        batches: initialData.purchasedBatches.batches
      } : undefined,
      ...QUERY_CONFIG,
      retry: 1
    }, {
      queryKey: PROFILE_QUERY_KEYS.referrals,
      queryFn: async () => {
        const response = await getReferrals();
        if (isActionError(response)) {
          throw new Error(response.error);
        }
        return response;
      },
      initialData: initialData.referrals,
      placeholderData: initialData.referrals,
      ...QUERY_CONFIG,
      retry: 1
    }, {
      queryKey: PROFILE_QUERY_KEYS.withdrawals,
      queryFn: async () => {
        const response = await getWithdrawals();
        if (isActionError(response)) {
          throw new Error(response.error);
        }
        return response?.withdrawals;
      },
      initialData: initialData.withdrawals,
      placeholderData: initialData.withdrawals,
      ...QUERY_CONFIG,
      retry: 1
    }]
  });

  /** Return processed profile data with additional metadata */
  const processedData = {
    user: user ?? initialData.user,
    paymentMethods: paymentMethods ?? initialData.paymentMethods,
    purchasedBatches: purchasedBatches?.batches ?? initialData.purchasedBatches.batches,
    referrals: referrals ?? initialData.referrals,
    withdrawals: withdrawals ?? initialData.withdrawals
  };
  return processedData;
}